import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import { Link, useLoaderData, useSearchParams } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { DocSidebarNav } from "#app/components/docs/DocSidebarNav";
import { DocTutorialShell } from "#app/components/docs/DocTutorialShell";
import { BrandCard } from "#app/components/ui/brand-card";
import {
  CardContent,
  CardHeader,
  CardTitle,
} from "#app/components/ui/card-legacy";
import { getRequestLocale } from "#app/i18n/get-request-locale.server";
import i18next from "#app/i18n/i18next.server";
import { loadDocIndex, type DocIndexItem } from "#app/lib/docs/loader";

export const handle = { i18n: ["docs", "common"] };

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const isJa = data?.locale === "ja";
  return [
    {
      title: isJa ? "ドキュメント | Supervity" : "Documentation | Supervity",
    },
    {
      name: "description",
      content: isJa
        ? "Supervityの製品ガイド、操作手順、リファレンス資料。"
        : "Browse Supervity documentation and product guides.",
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const locale = await getRequestLocale(request);
  await i18next.getFixedT(request, "docs");
  const docs = await loadDocIndex(locale);
  return json({ docs, locale });
}

export default function DocsIndex() {
  const { t } = useTranslation("docs");
  const { docs } = useLoaderData<typeof loader>() as {
    docs: DocIndexItem[];
    locale: string;
  };
  const [searchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";

  const query = q.trim().toLowerCase();
  const visibleDocs = query
    ? docs.filter((d) => {
        const haystack = [
          d.metadata.title,
          d.metadata.description,
          d.metadata.category,
          d.slug,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      })
    : docs;

  const grouped = visibleDocs.reduce<Record<string, DocIndexItem[]>>(
    (acc, d) => {
      const key = (d.metadata.category || "Other").trim() || "Other";
      acc[key] ||= [];
      acc[key]!.push(d);
      return acc;
    },
    {},
  );

  // Match the sidebar/mock ordering (first → last).
  const categoryOrder = [
    "Start Here",
    "Product Guide",
    "Integration Guides",
    "Guides",
    "How-to",
    "Reference",
    "About",
    "Other",
  ];

  const categoriesInOrder = categoryOrder.filter((cat) => grouped[cat]?.length);
  const extraCategories = Object.keys(grouped)
    .filter((cat) => !categoryOrder.includes(cat))
    .sort((a, b) => a.localeCompare(b));
  const categories = [...categoriesInOrder, ...extraCategories];
  const categoryLabels: Record<string, string> = {
    "Start Here": t("categories.startHere"),
    "Product Guide": t("categories.productGuide"),
    "Integration Guides": t("categories.integrationGuides"),
    Guides: t("categories.guides"),
    "How-to": t("categories.howTos"),
    Reference: t("categories.reference"),
    About: t("categories.about"),
    Other: t("categories.other"),
  };

  return (
    <DocTutorialShell activeTab="docs" leftNav={<DocSidebarNav docs={docs} />}>
      <div className="space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl font-bold text-[#141A42] dark:text-zinc-100">
            {t("documentation")}
          </h1>
          <p className="text-muted-foreground">{t("docsSubtitle")}</p>
        </div>

        {query ? (
          <div className="text-sm text-muted-foreground">
            {t("showingResultsFor", { query: q.trim() })}
          </div>
        ) : null}

        {query && visibleDocs.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-6 text-sm text-gray-700">
            {t("noDocsMatched")}
          </div>
        ) : null}

        {categories.map((cat) => (
          <section key={cat} className="space-y-4">
            <h2 className="text-2xl font-semibold text-[#141A42] dark:text-zinc-100">
              {categoryLabels[cat] ?? cat}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {grouped[cat]!.map((d) => (
                <Link key={d.slug} to={`/docs/${d.slug}`} className="block">
                  <BrandCard className="hover:border-[#D0D6ED]">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {d.metadata.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {d.metadata.description}
                      </p>
                    </CardContent>
                  </BrandCard>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </DocTutorialShell>
  );
}

export function shouldRevalidate() {
  // Docs index data does not depend on `q` query param.
  // Filtering happens client-side, so avoid revalidating on keystrokes.
  return false;
}
